import { t } from '@hefe/i18n';
import { BasketballGame, EventReportVisibility } from '@hefe/models';
import { EventReportsSlice } from '@hefe/store';
import { Button, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import CustomForm from '../../../../core/components/form/custom-form';
import GenerateEventReportWizardPrivacyComponent from '../generate-event-report-wizard-privacy/generate-event-report-wizard-privacy.component';

export const GENERATE_EVENT_REPORT_WIZARD_GENERAL_FORM_ID =
  'generate-event-report-wizard-general-form';

interface GenerateEventReportWizardGeneralFormComponentProps {
  game: BasketballGame;
}

export default function GenerateEventReportWizardGeneralFormComponent(
  props: GenerateEventReportWizardGeneralFormComponentProps
) {
  const dispatch = useAppDispatch();
  const wizard = useSelector(EventReportsSlice.generateWizardSelector);

  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  const onSubmit = (values: any, resetFields?: () => void) => {
    // store values in redux and go to next step
    dispatch(
      EventReportsSlice.actions.setGeneralInfo({
        type: values.type,
        writing_type: values.writing_type,
        visibility: values.is_public
          ? EventReportVisibility.PUBLIC
          : EventReportVisibility.PRIVATE,
      })
    );
  };

  const writingTypeOptions = useMemo(() => {
    const options = [
      {
        value: 'neutral',
        label: t(
          'game_reports.generate.wizard.steps.general.writing_type.neutral'
        ),
      },
    ];

    if (props.game?.team_details) {
      for (const team of props.game.team_details) {
        options.push({
          value: team.team?.name ?? 'neutral',
          label: team.team?.name ?? 'Mannschaft ' + team.id,
        });
      }
    }

    return options;
  }, [props.game]);

  return (
    <div>
      <CustomForm
        fields={[
          {
            name: 'type',
            type: 'select',
            label: t('game_reports.generate.wizard.steps.general.type.title'),
            initalValue: wizard?.data?.type,
            selectOptions: [
              {
                value: 'pre',
                label: t('game_reports.generate.wizard.steps.general.type.pre'),
              },
              {
                value: 'post',
                label: t(
                  'game_reports.generate.wizard.steps.general.type.post'
                ),
              },
            ],
            rules: [
              {
                required: true,
                message: t('form_errors.value'),
              },
            ],
          },
          {
            name: 'writing_type',
            type: 'select',
            label: t(
              'game_reports.generate.wizard.steps.general.writing_type.title'
            ),
            initalValue: wizard?.data?.writing_type,
            extraLabel: t(
              'game_reports.generate.wizard.steps.general.writing_type.description'
            ),
            selectOptions: writingTypeOptions,
            rules: [
              {
                required: true,
                message: t('form_errors.value'),
              },
            ],
          },
          {
            name: 'is_public',
            label: t('game_reports.generate.wizard.steps.general.public.title'),
            extraLabel: t(
              'game_reports.generate.wizard.steps.general.public.description'
            ),
            type: 'checkbox',
            initalValue: wizard?.data?.visibility,
            rules: [],
          },
          {
            name: 'privacy',
            label: '',
            extraLabel: (
              <Typography.Paragraph>
                {t(
                  'game_reports.generate.wizard.steps.general.privacy.description'
                )}
                <Button type="link" onClick={() => setPrivacyModalOpen(true)}>
                  {t('game_reports.generate.wizard.steps.general.privacy.link')}
                </Button>
              </Typography.Paragraph>
            ),
            type: 'checkbox',
            rules: [
              {
                required: true,
                transform: (value) => value || undefined,
                type: 'boolean',
                message: t('form_errors.user_consent'),
              },
            ],
          },
        ]}
        onSubmit={onSubmit}
        formId={GENERATE_EVENT_REPORT_WIZARD_GENERAL_FORM_ID}
      />
      <Typography.Paragraph>
        {t('game_reports.generate.wizard.steps.general.info')}
      </Typography.Paragraph>
      <GenerateEventReportWizardPrivacyComponent
        modalOpen={privacyModalOpen}
        closeModal={() => setPrivacyModalOpen(false)}
      />
    </div>
  );
}
